import { useGetContent } from '@/lib/react-query/ContentQueries';
import type { Content, Horaire } from '@/types/content.types';
import type { ProductWithCategory } from '@/types/product.types';

const APP_URL = import.meta.env.VITE_APP_URL || 'https://azelysse.fr';

const DAY_MAP: Record<string, string> = {
  Lundi: 'Monday',
  Mardi: 'Tuesday',
  Mercredi: 'Wednesday',
  Jeudi: 'Thursday',
  Vendredi: 'Friday',
  Samedi: 'Saturday',
  Dimanche: 'Sunday',
};

function buildOpeningHours(horaires: Array<Horaire>) {
  return horaires
    .filter((h) => h.is_open && h.start_time && h.end_time)
    .map((h) => ({
      '@type': 'OpeningHoursSpecification' as const,
      dayOfWeek: DAY_MAP[h.day] || h.day,
      opens: h.start_time,
      closes: h.end_time,
    }));
}

function buildLocalBusinessJsonLd(content: Content) {
  const contact = content.contact;
  const google = content.google;
  const instagrams = content.instagram || [];
  const horaires = content.horaire || [];

  const sameAs = instagrams.filter((i) => i.url).map((i) => i.url as string);

  return {
    '@context': 'https://schema.org',
    '@type': 'TattooParlor',
    name: 'Azelysse Piercing & Tattoo',
    description:
      "Studio de piercing et tatouage à Égly (91520), Essonne. Piercing personnalisé avec bijoux en titane ASTM F-136. À 5min d'Arpajon et 10min de Brétigny-sur-Orge.",
    url: APP_URL,
    logo: `${APP_URL}/logo.png`,
    image: `${APP_URL}/og-image-v2.png`,
    ...(contact?.phone && { telephone: contact.phone }),
    ...(contact?.email && { email: contact.email }),
    ...(contact?.address && {
      address: {
        '@type': 'PostalAddress',
        streetAddress: contact.address,
        addressLocality: 'Égly',
        postalCode: '91520',
        addressRegion: 'Île-de-France',
        addressCountry: 'FR',
      },
    }),
    ...(google?.maps_coordinate_lat &&
      google.maps_coordinate_lng && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: google.maps_coordinate_lat,
          longitude: google.maps_coordinate_lng,
        },
      }),
    priceRange: '€€',
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Cash, Credit Card',
    areaServed: [
      { '@type': 'City', name: 'Égly' },
      { '@type': 'City', name: 'Arpajon' },
      { '@type': 'City', name: 'Brétigny-sur-Orge' },
      { '@type': 'City', name: 'La Norville' },
      { '@type': 'City', name: 'Ollainville' },
      { '@type': 'City', name: 'Saint-Germain-lès-Arpajon' },
      { '@type': 'City', name: 'Breuillet' },
      { '@type': 'City', name: 'Linas' },
      { '@type': 'City', name: 'Montlhéry' },
      { '@type': 'City', name: 'Sainte-Geneviève-des-Bois' },
      { '@type': 'City', name: 'Les Ulis' },
      { '@type': 'City', name: 'Massy' },
      { '@type': 'City', name: 'Étampes' },
    ],
    hasMap: 'https://maps.google.com/?q=Azelysse+Piercing+Égly',
    ...(sameAs.length > 0 && { sameAs }),
    ...(horaires.length > 0 && {
      openingHoursSpecification: buildOpeningHours(horaires),
    }),
  };
}

export const JsonLdLocalBusiness = ({ content }: { content: Content }) => {
  const jsonLd = buildLocalBusinessJsonLd(content);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
};

export const JsonLdFaqPage = ({ faqs }: { faqs: Array<{ label: string; content: string }> }) => {
  if (faqs.length === 0) return null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.label,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.content,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
};

export const JsonLdProducts = ({ products }: { products: Array<ProductWithCategory> }) => {
  if (products.length === 0) return null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    name: 'Piercings & Tatouages - Azelysse',
    itemListElement: products.map((product) => ({
      '@type': 'Offer',
      name: product.name,
      price: product.price,
      priceCurrency: 'EUR',
      ...(product.description && { description: product.description }),
      ...(product.image_url && { image: product.image_url }),
      ...(product.category && { category: product.category.name }),
      availability: 'https://schema.org/InStock',
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
};

export const JsonLdBreadcrumb = ({ items }: { items: Array<{ name: string; path: string }> }) => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${APP_URL}${item.path}`,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
};

export const JsonLdWrapper = () => {
  const { data: content } = useGetContent();

  if (!content) return null;

  return <JsonLdLocalBusiness content={content} />;
};
